/**
 * Pantry Page – v2
 * Manages each client's pantry (špajza) with:
 *  - Multiple storage locations (Chladnička, Mraznička, Špajza, …)
 *  - Pantry items with amounts (g/kg/ks/balenie), expiration dates
 *  - Items grouped by storage location
 *  - Edit / delete per item
 *  - Add item dialog with food search, unit selection, optional expiry
 */
import { useState, useMemo, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { usePermissions } from "@/_core/hooks/usePermissions";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShoppingBasket,
  Plus,
  X,
  Search,
  User,
  Users,
  Pencil,
  Trash2,
  Warehouse,
  Package,
  CalendarClock,
  AlertTriangle,
} from "lucide-react";
import { ItemDialog } from "./components/ItemDialog";
import { UloziskoManager } from "./components/UloziskoManager";
import { ItemRow } from "./components/ItemRow";
import type { PantryItem, Ulozisko } from "./types";
import { formatAmount } from "./utils";

// ── Main Page ────────────────────────────────────────────────────────────────
export default function PantryPage() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const canManagePantryGroups = permissions.canListAllClients;
  const [selectedKlientId, setSelectedKlientId] = useState<number | null>(null);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<PantryItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<PantryItem | null>(null);
  const [showUloziskoManager, setShowUloziskoManager] = useState(false);
  const [newPantryGroupName, setNewPantryGroupName] = useState("");
  const [selectedPantryGroupId, setSelectedPantryGroupId] = useState<
    number | null
  >(null);
  const [pantryGroupClientId, setPantryGroupClientId] = useState<number | null>(
    null
  );

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data: allKlienti = [] } = trpc.klient.list.useQuery();
  const klienti = allKlienti.filter(k => k.odberStrava);
  const selectedKlient = klienti.find(k => k.id === selectedKlientId);

  const { data: uloziska = [], isLoading: uloziskaLoading } =
    trpc.spajza.getUloziska.useQuery(
      { klientId: selectedKlientId! },
      { enabled: !!selectedKlientId }
    );

  const { data: polozky = [], isLoading: polozkyLoading } =
    trpc.spajza.getPolozky.useQuery(
      { klientId: selectedKlientId! },
      { enabled: !!selectedKlientId }
    );

  const { data: currentPantryGroup } =
    trpc.spajza.getPantryGroupForClient.useQuery(
      { klientId: selectedKlientId! },
      { enabled: !!selectedKlientId }
    );

  const { data: pantryGroups = [] } = trpc.spajza.listPantryGroups.useQuery(
    undefined,
    { enabled: canManagePantryGroups }
  );

  const utils = trpc.useUtils();
  const deleteMutation = trpc.spajza.deletePolozka.useMutation();
  const createPantryGroupMutation = trpc.spajza.createPantryGroup.useMutation();
  const addClientToPantryGroupMutation =
    trpc.spajza.addClientToPantryGroup.useMutation();
  const removeClientFromPantryGroupMutation =
    trpc.spajza.removeClientFromPantryGroup.useMutation();

  const invalidate = () => {
    if (selectedKlientId) {
      utils.spajza.getPolozky.invalidate({ klientId: selectedKlientId });
      utils.spajza.getAvailability.invalidate({ klientId: selectedKlientId });
    }
  };

  const invalidateUloziska = () => {
    if (selectedKlientId) {
      utils.spajza.getUloziska.invalidate({ klientId: selectedKlientId });
    }
  };

  const invalidatePantryGroups = () => {
    utils.spajza.listPantryGroups.invalidate();
    if (selectedKlientId) {
      utils.spajza.getPantryGroupForClient.invalidate({
        klientId: selectedKlientId,
      });
      utils.spajza.getPolozky.invalidate({ klientId: selectedKlientId });
      utils.spajza.getUloziska.invalidate({ klientId: selectedKlientId });
      utils.spajza.getAvailability.invalidate({ klientId: selectedKlientId });
    }
  };

  // ── Sort state ───────────────────────────────────────────────────────────
  const [sortBy, setSortBy] = useState<"name" | "expiry">("expiry");

  // ── Group items by storage location ──────────────────────────────────────
  const groupedItems = useMemo(() => {
    const sortItems = (items: PantryItem[]) =>
      [...items].sort((a, b) => {
        if (sortBy === "name") {
          return a.potravina.nazov.localeCompare(b.potravina.nazov);
        }
        // sort by expiry: null expiry goes last
        if (!a.datumSpotreby && !b.datumSpotreby) return 0;
        if (!a.datumSpotreby) return 1;
        if (!b.datumSpotreby) return -1;
        return (
          new Date(a.datumSpotreby).getTime() -
          new Date(b.datumSpotreby).getTime()
        );
      });

    const groups: { ulozisko: Ulozisko | null; items: PantryItem[] }[] = [];

    // Items with a location (in location order)
    for (const u of uloziska) {
      const items = polozky.filter(p => p.uloziskoId === u.id);
      if (items.length > 0) {
        groups.push({
          ulozisko: u as Ulozisko,
          items: sortItems(items as PantryItem[]),
        });
      }
    }
    // Items without a location
    const unassigned = polozky.filter(p => p.uloziskoId === null);
    if (unassigned.length > 0) {
      groups.push({
        ulozisko: null,
        items: sortItems(unassigned as PantryItem[]),
      });
    }
    return groups;
  }, [polozky, uloziska, sortBy]);

  const formatGroupMember = (member: {
    alias: string;
    meno: string | null;
    priezvisko: string | null;
  }) => {
    const fullName = [member.meno, member.priezvisko].filter(Boolean).join(" ");
    return fullName ? `${member.alias} (${fullName})` : member.alias;
  };

  const selectedAdminGroup = pantryGroups.find(
    group => group.id === selectedPantryGroupId
  );

  const availableClientsForSelectedGroup = klienti.filter(
    client =>
      !selectedAdminGroup?.clients.some(member => member.klientId === client.id)
  );

  const handleCreatePantryGroup = async () => {
    const nazov = newPantryGroupName.trim();
    if (!nazov) return;
    try {
      const group = await createPantryGroupMutation.mutateAsync({ nazov });
      setNewPantryGroupName("");
      setSelectedPantryGroupId(group.id);
      toast.success(t("mealLibrary:spajza.pantryGroupCreated"));
      invalidatePantryGroups();
    } catch {
      toast.error(t("mealLibrary:spajza.pantryGroupCreateError"));
    }
  };

  const handleAddClientToPantryGroup = async () => {
    if (!selectedPantryGroupId || !pantryGroupClientId) return;
    try {
      await addClientToPantryGroupMutation.mutateAsync({
        pantryGroupId: selectedPantryGroupId,
        klientId: pantryGroupClientId,
      });
      setPantryGroupClientId(null);
      toast.success(t("mealLibrary:spajza.pantryGroupClientAdded"));
      invalidatePantryGroups();
    } catch {
      toast.error(t("mealLibrary:spajza.pantryGroupClientAddError"));
    }
  };

  const handleRemoveClientFromPantryGroup = async (
    pantryGroupId: number,
    klientId: number
  ) => {
    try {
      await removeClientFromPantryGroupMutation.mutateAsync({
        pantryGroupId,
        klientId,
      });
      toast.success(t("mealLibrary:spajza.pantryGroupClientRemoved"));
      invalidatePantryGroups();
    } catch {
      toast.error(t("mealLibrary:spajza.pantryGroupClientRemoveError"));
    }
  };

  const handleDelete = async (item: PantryItem) => {
    try {
      await deleteMutation.mutateAsync({
        id: item.id,
        klientId: item.klientId,
      });
      toast.success(t("mealLibrary:spajza.itemDeleted"));
      invalidate();
    } catch {
      toast.error(t("mealLibrary:spajza.itemDeleteError"));
    }
    setDeleteItem(null);
  };

  const isLoading = polozkyLoading || uloziskaLoading;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Page header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingBasket className="h-6 w-6" />
            {t("mealLibrary:spajza.title")}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t("mealLibrary:spajza.pageSubtitle")}
          </p>
        </div>

        {/* Client selector */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-4 w-4" />
              {t("common:client")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select
              value={selectedKlientId ? String(selectedKlientId) : ""}
              onValueChange={v => {
                setSelectedKlientId(v ? Number(v) : null);
              }}
            >
              <SelectTrigger className="w-full max-w-sm">
                <SelectValue placeholder={t("intake:filters.filterByClient")} />
              </SelectTrigger>
              <SelectContent>
                {klienti.map(k => (
                  <SelectItem key={k.id} value={String(k.id)}>
                    {k.alias}
                    {(k.meno || k.priezvisko) && (
                      <span className="ml-2 text-muted-foreground text-xs">
                        {[k.meno, k.priezvisko].filter(Boolean).join(" ")}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {canManagePantryGroups && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4" />
                {t("mealLibrary:spajza.pantryGroups")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2 md:grid-cols-[1fr_auto]">
                <Input
                  value={newPantryGroupName}
                  onChange={event => setNewPantryGroupName(event.target.value)}
                  placeholder={t(
                    "mealLibrary:spajza.pantryGroupNamePlaceholder"
                  )}
                />
                <Button
                  type="button"
                  onClick={handleCreatePantryGroup}
                  disabled={
                    !newPantryGroupName.trim() ||
                    createPantryGroupMutation.isPending
                  }
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t("mealLibrary:spajza.createPantryGroup")}
                </Button>
              </div>

              {pantryGroups.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  {t("mealLibrary:spajza.noPantryGroups")}
                </p>
              ) : (
                <div className="space-y-3">
                  <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                    <Select
                      value={
                        selectedPantryGroupId
                          ? String(selectedPantryGroupId)
                          : ""
                      }
                      onValueChange={value => {
                        setSelectedPantryGroupId(value ? Number(value) : null);
                        setPantryGroupClientId(null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t(
                            "mealLibrary:spajza.selectPantryGroup"
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {pantryGroups.map(group => (
                          <SelectItem key={group.id} value={String(group.id)}>
                            {group.nazov}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={
                        pantryGroupClientId ? String(pantryGroupClientId) : ""
                      }
                      onValueChange={value =>
                        setPantryGroupClientId(value ? Number(value) : null)
                      }
                      disabled={!selectedPantryGroupId}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t(
                            "mealLibrary:spajza.selectClientForGroup"
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {availableClientsForSelectedGroup.map(client => (
                          <SelectItem key={client.id} value={String(client.id)}>
                            {client.alias}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddClientToPantryGroup}
                      disabled={
                        !selectedPantryGroupId ||
                        !pantryGroupClientId ||
                        addClientToPantryGroupMutation.isPending
                      }
                    >
                      {t("mealLibrary:spajza.addClientToGroup")}
                    </Button>
                  </div>

                  {pantryGroups.map(group => (
                    <div
                      key={group.id}
                      className="rounded-md border p-3 space-y-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium">{group.nazov}</div>
                        <Badge variant="secondary">
                          {t("mealLibrary:spajza.groupMemberCount", {
                            count: group.clients.length,
                          })}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {group.clients.length === 0 ? (
                          <span className="text-xs text-muted-foreground italic">
                            {t("mealLibrary:spajza.noGroupMembers")}
                          </span>
                        ) : (
                          group.clients.map(member => (
                            <Badge
                              key={member.klientId}
                              variant="outline"
                              className="gap-1"
                            >
                              {formatGroupMember(member)}
                              <button
                                type="button"
                                className="ml-1 rounded-full hover:text-destructive"
                                onClick={() =>
                                  handleRemoveClientFromPantryGroup(
                                    group.id,
                                    member.klientId
                                  )
                                }
                                aria-label={t(
                                  "mealLibrary:spajza.removeClientFromGroup"
                                )}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pantry card – only shown once a client is selected */}
        {selectedKlientId && (
          <>
            {/* Storage locations manager */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Warehouse className="h-4 w-4" />
                  {t("mealLibrary:spajza.storages")}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowUloziskoManager(v => !v)}
                >
                  {showUloziskoManager
                    ? t("mealLibrary:spajza.hideStorageManager")
                    : t("mealLibrary:spajza.manageStorages")}
                </Button>
              </CardHeader>
              {showUloziskoManager && (
                <CardContent>
                  <UloziskoManager
                    klientId={selectedKlientId}
                    uloziska={uloziska as Ulozisko[]}
                    onChanged={invalidateUloziska}
                  />
                </CardContent>
              )}
            </Card>

            {/* Main pantry card */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <ShoppingBasket className="h-5 w-5" />
                  {selectedKlient?.alias ?? ""}
                  <span className="text-sm font-normal text-muted-foreground ml-1">
                    — {t("mealLibrary:spajza.currentItems")} ({polozky.length})
                  </span>
                </CardTitle>
                {currentPantryGroup && (
                  <Badge variant="secondary" className="ml-auto mr-2 gap-1">
                    <Users className="h-3 w-3" />
                    {t("mealLibrary:spajza.sharedPantryGroupBadge", {
                      name: currentPantryGroup.nazov,
                    })}
                  </Badge>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditItem(null);
                    setItemDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t("mealLibrary:spajza.addFood")}
                </Button>
              </CardHeader>
              <CardContent>
                {currentPantryGroup && (
                  <div className="mb-4 rounded-md border bg-muted/30 p-3 text-sm">
                    <div className="font-medium flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {t("mealLibrary:spajza.sharedPantryGroupTitle", {
                        name: currentPantryGroup.nazov,
                      })}
                    </div>
                    <p className="text-muted-foreground mt-1">
                      {t("mealLibrary:spajza.sharedPantryGroupDescription")}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {currentPantryGroup.clients.map(member => (
                        <Badge key={member.klientId} variant="outline">
                          {formatGroupMember(member)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {isLoading ? (
                  <p className="text-sm text-muted-foreground">
                    {t("common:loading")}
                  </p>
                ) : polozky.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic py-4 text-center">
                    {t("mealLibrary:spajza.empty")}
                  </p>
                ) : (
                  <div className="space-y-6">
                    {/* Sort controls */}
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">
                        {t("mealLibrary:spajza.sortBy")}:
                      </span>
                      <button
                        onClick={() => setSortBy("expiry")}
                        className={`px-2 py-0.5 rounded border text-xs transition-colors ${
                          sortBy === "expiry"
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border hover:border-primary"
                        }`}
                      >
                        {t("mealLibrary:spajza.sortExpiry")}
                      </button>
                      <button
                        onClick={() => setSortBy("name")}
                        className={`px-2 py-0.5 rounded border text-xs transition-colors ${
                          sortBy === "name"
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border hover:border-primary"
                        }`}
                      >
                        {t("mealLibrary:spajza.sortName")}
                      </button>
                    </div>
                    {groupedItems.map(({ ulozisko, items }) => (
                      <div key={ulozisko?.id ?? "unassigned"}>
                        {/* Section header */}
                        <div className="flex items-center gap-2 mb-2">
                          <Warehouse className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-semibold text-muted-foreground">
                            {ulozisko?.nazov ??
                              t("mealLibrary:spajza.noStorage")}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            ({items.length})
                          </span>
                        </div>
                        <div className="space-y-2">
                          {items.map(item => (
                            <ItemRow
                              key={item.id}
                              item={item}
                              onEdit={i => {
                                setEditItem(i);
                                setItemDialogOpen(true);
                              }}
                              onDelete={i => setDeleteItem(i)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* No client selected placeholder */}
        {!selectedKlientId && (
          <div className="text-center py-16 text-muted-foreground">
            <ShoppingBasket className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg">{t("intake:selectClient")}</p>
          </div>
        )}
      </div>

      {/* Add/Edit Item Dialog */}
      {selectedKlientId && (
        <ItemDialog
          key={editItem ? `edit-${editItem.id}` : "add-new"}
          open={itemDialogOpen}
          onOpenChange={open => {
            setItemDialogOpen(open);
            if (!open) setEditItem(null);
          }}
          klientId={selectedKlientId}
          uloziska={uloziska as Ulozisko[]}
          editItem={editItem}
          onSaved={invalidate}
        />
      )}

      {/* Delete confirm */}
      <AlertDialog
        open={deleteItem !== null}
        onOpenChange={o => !o && setDeleteItem(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("mealLibrary:spajza.deleteItem")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItem && (
                <>
                  {t("mealLibrary:spajza.deleteItemConfirmPrefix")}{" "}
                  <strong>{deleteItem.potravina.nazov}</strong> (
                  {formatAmount(deleteItem)})?
                  {parseFloat(deleteItem.rezervovaneG) > 0 && (
                    <span className="block mt-1 text-orange-600">
                      {t("mealLibrary:spajza.deleteReservedWarning")}
                    </span>
                  )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteItem && handleDelete(deleteItem)}
            >
              {t("mealLibrary:dialog.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
